import { SEO } from '../../../utils/seo';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const mapTypes = [
  {
    title: 'Orthomosaic Maps',
    subtitle: 'The Foundation of Field Intelligence',
    description: 'Orthomosaic maps stitch together hundreds of overlapping aerial images into a single, geometrically corrected, high-resolution view of your entire field. Unlike satellite imagery, these maps capture detail down to individual plants.',
    benefits: [
      'Stitched high-resolution field view with sub-inch accuracy',
      'Scouting and record-keeping for season-over-season comparison',
      'Saves up to 70% in field inspection costs',
      'Identify drainage issues, compaction zones, and stand variability'
    ],
    useCases: 'Ideal for baseline field documentation, insurance claims, boundary verification, and identifying problem areas before they spread.',
    image: 'https://public.readdy.ai/ai/img_res/991d62dd3671c65290b4e0acb1b8e64e.jpg'
  },
  {
    title: 'NDVI / NDRE Vegetation Index Maps',
    subtitle: 'See What Your Eyes Cannot',
    description: 'Normalized Difference Vegetation Index (NDVI) and Red Edge (NDRE) maps use multispectral sensors to measure plant health based on how crops reflect light. Stressed plants show up days or weeks before visible symptoms appear.',
    benefits: [
      'Detect crop stress 10-14 days before visible symptoms',
      'Boost yields 5-15% through early intervention',
      'Precision nutrient and pest management targeting',
      'Variable rate application zone mapping'
    ],
    useCases: 'Essential for identifying nitrogen deficiency, disease pressure, irrigation problems, and creating prescription maps for variable rate applications.',
    image: 'https://readdy.ai/api/search-image?query=NDVI%20vegetation%20index%20agricultural%20map%20showing%20color%20gradient%20from%20red%20stressed%20areas%20to%20bright%20green%20healthy%20zones%20multispectral%20drone%20imagery%20crop%20health%20analysis%20professional%20precision%20farming%20data%20visualization&width=800&height=600&seq=ndvi002&orientation=landscape'
  },
  {
    title: 'Thermal Imaging Maps',
    subtitle: 'Temperature Tells the Story',
    description: 'Thermal cameras detect temperature variations across your field that indicate irrigation efficiency, drainage problems, and plant stress. Water-stressed crops run hotter than healthy plants—thermal imaging reveals these patterns instantly.',
    benefits: [
      'Spot irrigation system failures and coverage gaps',
      'Save 20-30% on water usage through targeted management',
      'Reduce pumping costs with optimized irrigation scheduling',
      'Identify tile drainage issues and wet spots'
    ],
    useCases: 'Critical for irrigated operations, identifying failed emitters, mapping drainage patterns, and optimizing water application timing.',
    image: 'https://readdy.ai/api/search-image?query=thermal%20infrared%20drone%20map%20of%20agricultural%20field%20showing%20heat%20signature%20variations%20warm%20orange%20and%20cool%20blue%20zones%20indicating%20irrigation%20patterns%20and%20crop%20water%20stress%20professional%20precision%20agriculture%20thermal%20imaging&width=800&height=600&seq=thermal001&orientation=landscape'
  },
  {
    title: 'Elevation & Terrain Maps',
    subtitle: 'Understand Your Land in 3D',
    description: 'Digital elevation models (DEMs) and terrain maps reveal the topography of your fields with centimeter-level accuracy. Understanding water flow, erosion risk, and slope variations is essential for drainage planning and precision application.',
    benefits: [
      'Plan drainage improvements with accurate elevation data',
      'Reduce erosion through targeted conservation practices',
      'Optimize planting patterns for variable terrain',
      'Identify low spots prone to ponding and compaction'
    ],
    useCases: 'Valuable for drainage tile planning, terrace design, waterway placement, and understanding yield variability caused by topography.',
    image: 'https://readdy.ai/api/search-image?query=digital%20elevation%20terrain%20map%20of%20agricultural%20land%20showing%20topographic%20contours%20and%20height%20variations%20in%20green%20and%20tan%20colors%203D%20terrain%20visualization%20drone%20survey%20data%20professional%20land%20mapping&width=800&height=600&seq=elev001&orientation=landscape'
  }
];

const comparisonData = [
  { feature: 'Resolution', satellite: '30cm - 3m per pixel', drone: '1-3cm per pixel' },
  { feature: 'Timing Control', satellite: 'Fixed schedule, weather dependent', drone: 'On-demand, your schedule' },
  { feature: 'Cloud Interference', satellite: 'Frequent data gaps', drone: 'Fly below clouds' },
  { feature: 'Turnaround', satellite: '3-7 days typical', drone: 'Same day or next day' },
  { feature: 'Cost per Acre', satellite: '$0.50-2.00', drone: '$3-8 (higher value data)' },
  { feature: 'Multispectral Options', satellite: 'Limited bands', drone: 'Full spectrum flexibility' }
];

const roiStats = [
  { value: '70%', label: 'Reduction in scouting time', description: 'Cover more ground, find problems faster' },
  { value: '5-15%', label: 'Yield improvement potential', description: 'Through early stress detection and intervention' },
  { value: '20-30%', label: 'Water savings', description: 'With thermal-guided irrigation management' },
  { value: '2-3x', label: 'ROI on mapping investment', description: 'Typical first-season return for most operations' }
];

export default function DroneMapsBlogPage() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Drone Maps for Precision Agriculture: The Complete Guide to Aerial Field Intelligence",
    "description": "Learn how orthomosaic, NDVI, thermal, and elevation drone maps transform farm management. Comprehensive guide to agricultural drone mapping technology and ROI.",
    "image": "https://readdy.ai/api/search-image?query=professional%20agricultural%20drone%20flying%20over%20green%20crop%20field%20capturing%20aerial%20mapping%20data%20precision%20farming%20technology%20modern%20agriculture&width=1200&height=630&seq=ogimage001&orientation=landscape",
    "author": {
      "@type": "Organization",
      "name": "Greenfield Partners",
      "url": "https://greenfieldpartners.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Greenfield Partners",
      "logo": {
        "@type": "ImageObject",
        "url": "https://greenfieldpartners.com/logo.png"
      }
    },
    "datePublished": "2024-12-15",
    "dateModified": new Date().toISOString().split('T')[0],
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://greenfieldpartners.com/blog/drone-maps-precision-agriculture"
    },
    "keywords": "drone mapping agriculture, NDVI crop mapping, precision agriculture drones, agricultural drone surveys, farm aerial mapping, crop health monitoring"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is NDVI mapping and how does it help farmers?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "NDVI (Normalized Difference Vegetation Index) mapping uses multispectral drone sensors to measure plant health by analyzing how crops reflect near-infrared and red light. Healthy plants reflect more near-infrared light, while stressed plants reflect less. This allows farmers to detect crop stress 10-14 days before visible symptoms appear, enabling early intervention that can improve yields by 5-15%."
        }
      },
      {
        "@type": "Question",
        "name": "How accurate are drone maps compared to satellite imagery?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Drone maps typically achieve 1-3cm per pixel resolution, compared to 30cm-3m for satellite imagery. This 10-100x improvement in detail allows farmers to identify individual plant health, detect small problem areas, and create precise prescription maps for variable rate applications."
        }
      },
      {
        "@type": "Question",
        "name": "What is the ROI of agricultural drone mapping?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most farming operations see 2-3x return on their drone mapping investment in the first season. Benefits include 70% reduction in scouting time, 5-15% yield improvements through early stress detection, and 20-30% water savings with thermal-guided irrigation management."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO
        title="Drone Maps for Precision Agriculture: Complete Guide to NDVI, Thermal & Orthomosaic Mapping | Greenfield Partners"
        description="Discover how agricultural drone mapping transforms farm management. Learn about NDVI crop health maps, thermal irrigation analysis, orthomosaic surveys, and elevation terrain mapping. Save 70% on scouting costs and boost yields 5-15%."
        keywords="drone mapping agriculture, NDVI crop mapping, precision agriculture drones, agricultural drone surveys, farm aerial mapping, crop health monitoring, thermal imaging farms, orthomosaic mapping, drone elevation maps, variable rate application"
        schema={schema}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[75vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20agricultural%20drone%20with%20multispectral%20camera%20flying%20low%20over%20lush%20green%20crop%20field%20capturing%20precision%20mapping%20data%20golden%20hour%20lighting%20modern%20farming%20technology%20aerial%20view%20of%20expansive%20farmland&width=1920&height=1080&seq=heromain001&orientation=landscape"
            alt="Agricultural drone capturing precision mapping data over crop field"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="bg-emerald-700/90 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
                Precision Agriculture Guide
              </span>
              <span className="text-gray-300 text-sm">December 2024</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Drone Maps for<br />
              <span className="text-emerald-400">Precision Agriculture</span>
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed mb-8 max-w-2xl">
              How orthomosaic, NDVI, thermal, and elevation mapping transform field intelligence—and why the data you collect today shapes the yields you harvest tomorrow.
            </p>
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-300">
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-emerald-400"></i>
                <span>12 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-drone-line text-emerald-400"></i>
                <span>Mapping Technology</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-bar-chart-box-line text-emerald-400"></i>
                <span>ROI Analysis</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 animate-bounce">
          <i className="ri-arrow-down-line text-2xl"></i>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="prose prose-lg">
            <p className="text-xl text-gray-700 leading-relaxed mb-6 first-letter:text-5xl first-letter:font-bold first-letter:text-emerald-700 first-letter:float-left first-letter:mr-3 first-letter:mt-1">
              The difference between guessing and knowing is often measured in bushels per acre. For decades, farmers relied on windshield surveys, soil probes, and intuition to understand what was happening across their fields. Today, <strong>agricultural drone mapping</strong> delivers answers in hours that once took weeks to uncover.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              But not all drone maps are created equal. Understanding the difference between <strong>orthomosaic imagery</strong>, <strong>NDVI vegetation indices</strong>, <strong>thermal analysis</strong>, and <strong>elevation modeling</strong> is essential for extracting real value from aerial data. Each map type answers different questions—and the right combination can transform how you manage every acre.
            </p>
            <p className="text-gray-600 leading-relaxed">
              This guide breaks down the four essential drone map types for precision agriculture, explains when and why to use each, and shows you how to calculate the return on your mapping investment.
            </p>
          </div>

          {/* Quick Navigation */}
          <div className="mt-12 p-6 bg-stone-100 rounded-xl">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-list-check text-emerald-700"></i>
              In This Guide
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {mapTypes.map((type, index) => (
                <a
                  key={index}
                  href={`#${type.title.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')}`}
                  className="flex items-center gap-2 text-gray-700 hover:text-emerald-700 transition-colors cursor-pointer text-sm"
                >
                  <span className="w-6 h-6 bg-emerald-700 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </span>
                  {type.title}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Map Types Deep Dive */}
      {mapTypes.map((mapType, index) => (
        <section
          key={index}
          id={mapType.title.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')}
          className={`py-24 px-6 ${index % 2 === 0 ? 'bg-stone-100 topo-texture-light' : 'bg-white'}`}
        >
          <div className="relative z-10 max-w-7xl mx-auto">
            <div className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <span className="text-emerald-700 text-xs font-bold uppercase tracking-widest">
                  Map Type {String(index + 1).padStart(2, '0')}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-2">
                  {mapType.title}
                </h2>
                <p className="text-emerald-700 font-medium mb-6">{mapType.subtitle}</p>
                
                <p className="text-gray-600 leading-relaxed mb-8">
                  {mapType.description}
                </p>

                <div className="space-y-3 mb-8">
                  {mapType.benefits.map((benefit, bIndex) => (
                    <div key={bIndex} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center bg-emerald-100 rounded-full mt-0.5 shrink-0">
                        <i className="ri-check-line text-emerald-700 text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-emerald-50 border-l-4 border-emerald-700 p-4 rounded-r-lg">
                  <p className="text-sm text-gray-700">
                    <strong className="text-emerald-800">Best Use Cases:</strong> {mapType.useCases}
                  </p>
                </div>
              </div>

              {/* Image */}
              <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src={mapType.image}
                    alt={`${mapType.title} example showing agricultural drone mapping data for precision farming`}
                    className="w-full h-80 lg:h-96 object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                    <p className="text-white text-sm font-medium">{mapType.title}</p>
                    <p className="text-white/70 text-xs">{mapType.subtitle}</p>
                  </div>
                </div>
                
                {/* Decorative element */}
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-emerald-700/10 rounded-xl -z-10"></div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Comparison Section */}
      <section className="py-24 px-6 topo-cta-bg">
        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-emerald-400 text-xs font-bold uppercase tracking-widest">Data Quality Matters</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4">
              Drone Mapping vs. Satellite Imagery
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Both have their place, but understanding the differences helps you choose the right tool for each decision.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden">
            <div className="grid grid-cols-3 bg-white/5 p-4 border-b border-white/10">
              <div className="text-white font-semibold">Feature</div>
              <div className="text-center text-gray-300 font-medium">Satellite</div>
              <div className="text-center text-emerald-400 font-medium">Drone Mapping</div>
            </div>
            {comparisonData.map((row, index) => (
              <div
                key={index}
                className={`grid grid-cols-3 p-4 ${index !== comparisonData.length - 1 ? 'border-b border-white/10' : ''}`}
              >
                <div className="text-white text-sm font-medium">{row.feature}</div>
                <div className="text-center text-gray-400 text-sm">{row.satellite}</div>
                <div className="text-center text-emerald-300 text-sm font-medium">{row.drone}</div>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-400 text-sm mt-6">
            Satellite imagery works well for broad regional monitoring. Drone mapping excels when you need actionable, field-level precision.
          </p>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-24 px-6 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-emerald-700 text-xs font-bold uppercase tracking-widest">The Business Case</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              ROI of Agricultural Drone Mapping
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The numbers speak for themselves. Here's what operations typically see in their first season with comprehensive drone mapping.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {roiStats.map((stat, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-emerald-700 mb-2">{stat.value}</div>
                <div className="text-gray-900 font-semibold mb-2">{stat.label}</div>
                <div className="text-gray-500 text-sm">{stat.description}</div>
              </div>
            ))}
          </div>

          {/* Case Example */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="grid lg:grid-cols-2">
              <div className="p-8 lg:p-12">
                <span className="text-emerald-700 text-xs font-bold uppercase tracking-widest">Real Example</span>
                <h3 className="text-2xl font-bold text-gray-900 mt-2 mb-4">
                  500-Acre Corn Operation
                </h3>
                <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
                  <p>
                    A Mid-Atlantic corn grower invested in quarterly NDVI mapping across 500 acres. The first flight revealed a 40-acre zone showing early nitrogen stress—invisible from the ground.
                  </p>
                  <p>
                    Targeted side-dress application to that zone cost $1,200. Without intervention, estimated yield loss would have exceeded $8,000 based on historical data from similar stress patterns.
                  </p>
                  <p>
                    <strong className="text-gray-900">Net benefit from single intervention: $6,800</strong>
                  </p>
                  <p>
                    The annual mapping investment paid for itself three times over from one early detection.
                  </p>
                </div>
              </div>
              <div className="relative h-64 lg:h-auto">
                <img
                  src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20healthy%20green%20corn%20field%20with%20visible%20rows%20stretching%20to%20horizon%20golden%20hour%20lighting%20professional%20agricultural%20photography%20showing%20successful%20crop%20management%20precision%20farming%20results&width=800&height=600&seq=casestudy001&orientation=landscape"
                  alt="Healthy corn field showing results of precision agriculture management"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Report Download Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-700 text-xs font-bold uppercase tracking-widest">Free Resource</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Sample Orthomosaic Report
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              See exactly what you'll receive with our mapping services. Download a real sample report from a 320-acre soybean field survey.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Report Preview */}
            <div className="relative">
              <div className="bg-gradient-to-br from-stone-100 to-stone-200 rounded-2xl p-6 shadow-lg">
                {/* Mock PDF Preview */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  {/* Header */}
                  <div className="bg-emerald-800 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                          <i className="ri-drone-line text-white text-lg"></i>
                        </div>
                        <div>
                          <p className="text-white font-bold text-sm">Greenfield Partners</p>
                          <p className="text-emerald-200 text-xs">Orthomosaic Field Report</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white text-xs font-medium">Sample Report</p>
                        <p className="text-emerald-200 text-xs">320 Acres • Soybeans</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content Preview */}
                  <div className="p-6">
                    <div className="aspect-video bg-stone-100 rounded-lg mb-4 overflow-hidden relative">
                      <img
                        src="https://readdy.ai/api/search-image?query=high%20resolution%20drone%20orthomosaic%20map%20of%20soybean%20agricultural%20field%20showing%20perfectly%20stitched%20aerial%20view%20with%20visible%20crop%20rows%20geometric%20precision%20top%20down%20perspective%20green%20farmland%20professional%20precision%20agriculture%20mapping%20survey%20document%20style&width=600&height=340&seq=samplereport001&orientation=landscape"
                        alt="Sample orthomosaic map preview"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        1.2cm/pixel resolution
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-stone-50 rounded-lg p-3 text-center">
                        <p className="text-emerald-700 font-bold text-lg">320</p>
                        <p className="text-gray-500 text-xs">Acres Mapped</p>
                      </div>
                      <div className="bg-stone-50 rounded-lg p-3 text-center">
                        <p className="text-emerald-700 font-bold text-lg">847</p>
                        <p className="text-gray-500 text-xs">Images Stitched</p>
                      </div>
                      <div className="bg-stone-50 rounded-lg p-3 text-center">
                        <p className="text-emerald-700 font-bold text-lg">98.7%</p>
                        <p className="text-gray-500 text-xs">Coverage</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 text-gray-400 text-xs">
                      <i className="ri-file-pdf-2-line"></i>
                      <span>sample-orthomosaic-report.pdf • 4.2 MB</span>
                    </div>
                  </div>
                </div>
                
                {/* Decorative pages behind */}
                <div className="absolute -bottom-2 -right-2 w-full h-full bg-white rounded-xl shadow-md -z-10 transform rotate-2"></div>
                <div className="absolute -bottom-4 -right-4 w-full h-full bg-stone-50 rounded-xl shadow -z-20 transform rotate-4"></div>
              </div>
            </div>

            {/* Report Details */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">What's Included in the Report</h3>
              
              <div className="space-y-4 mb-8">
                {[
                  {
                    icon: 'ri-map-2-line',
                    title: 'High-Resolution Orthomosaic Map',
                    desc: 'Full-field stitched imagery at 1.2cm/pixel resolution with GPS coordinates'
                  },
                  {
                    icon: 'ri-bar-chart-grouped-line',
                    title: 'Field Statistics Summary',
                    desc: 'Total acreage, coverage percentage, flight parameters, and data quality metrics'
                  },
                  {
                    icon: 'ri-focus-3-line',
                    title: 'Problem Area Annotations',
                    desc: 'Highlighted zones showing drainage issues, stand gaps, and areas of concern'
                  },
                  {
                    icon: 'ri-compass-3-line',
                    title: 'Boundary & Reference Data',
                    desc: 'Field boundaries, scale bars, north arrow, and coordinate reference system'
                  },
                  {
                    icon: 'ri-download-cloud-line',
                    title: 'GeoTIFF Export Information',
                    desc: 'Instructions for importing into farm management software platforms'
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                      <i className={`${item.icon} text-emerald-700 text-lg`}></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{item.title}</p>
                      <p className="text-gray-500 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <a
                href="https://drive.google.com/file/d/1example-sample-orthomosaic-report/view"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-6 py-4 bg-emerald-700 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap shadow-lg shadow-emerald-700/20"
              >
                <i className="ri-file-pdf-2-line text-xl"></i>
                Download Sample Report (PDF)
                <i className="ri-external-link-line text-sm opacity-70"></i>
              </a>
              
              <p className="text-gray-500 text-xs mt-4 flex items-center gap-2">
                <i className="ri-lock-line"></i>
                No email required • Free instant download • 4.2 MB PDF
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Getting Started Section */}
      <section className="py-24 px-6 bg-stone-100 topo-texture-light">
        <div className="relative z-10 max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-700 text-xs font-bold uppercase tracking-widest">Next Steps</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Getting Started with Drone Mapping
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Ready to see what aerial intelligence can do for your operation? <a href="/services/agriculture" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">Explore our agriculture services <i className="ri-arrow-right-line"></i></a> or <a href="/roi-calculator" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">calculate your potential ROI <i className="ri-arrow-right-line"></i></a>.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                <span className="text-emerald-700 font-bold text-xl">1</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Define Your Questions</h4>
              <p className="text-gray-600 text-sm">
                What problems are you trying to solve? Yield variability? Irrigation efficiency? Pest pressure? The right map type depends on the questions you need answered.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                <span className="text-emerald-700 font-bold text-xl">2</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Plan Your Timing</h4>
              <p className="text-gray-600 text-sm">
                Early season baseline, two mid-season stress checks during critical growth stages, and a pre-harvest assessment each serve different purposes. Map when the data will drive decisions.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                <span className="text-emerald-700 font-bold text-xl">3</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Partner with Experts</h4>
              <p className="text-gray-600 text-sm">
                Data is only valuable if you can act on it. Work with a team that understands both the technology and the agronomy behind the numbers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-700 text-xs font-bold uppercase tracking-widest">Common Questions</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-3 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'How often should I map my fields?',
                a: 'Most operations benefit from 3-4 flights per season: early season baseline, two mid-season stress checks during critical growth stages, and a pre-harvest assessment. High-value crops or problem fields may warrant monthly monitoring.'
              },
              {
                q: 'What weather conditions are required for drone mapping?',
                a: 'Ideal conditions include winds under 15 mph, no precipitation, and consistent lighting (overcast days often produce better NDVI data than harsh midday sun). We monitor conditions and reschedule if quality would be compromised.'
              },
              {
                q: 'How quickly can I get my mapping data?',
                a: 'Orthomosaic maps are typically delivered within 24-48 hours. NDVI and thermal analysis may take an additional day for processing and interpretation. Rush delivery is available for time-sensitive decisions.'
              },
              {
                q: 'Can drone maps integrate with my existing farm management software?',
                a: 'Yes. We deliver data in standard formats (GeoTIFF, shapefiles) compatible with John Deere Operations Center, Climate FieldView, Trimble Ag Software, and most other precision ag platforms.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-stone-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-2 flex items-start gap-3">
                  <span className="text-emerald-700">Q:</span>
                  {faq.q}
                </h4>
                <p className="text-gray-600 text-sm leading-relaxed pl-6">
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 px-6">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20patchwork%20agricultural%20fields%20at%20sunset%20golden%20hour%20light%20casting%20warm%20glow%20over%20green%20and%20brown%20farmland%20professional%20landscape%20photography%20showing%20expansive%20productive%20farmland%20from%20drone%20perspective&width=1920&height=800&seq=ctafinal001&orientation=landscape"
            alt="Aerial view of agricultural fields at sunset"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
        </div>

        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <span className="text-emerald-400 text-sm font-semibold uppercase tracking-widest mb-4 block">
            Ready to See Your Fields Differently?
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Turn Data Into<br />
            <span className="text-emerald-400">Decisions That Pay</span>
          </h2>
          <p className="text-xl text-gray-200 mb-10 leading-relaxed">
            Whether you need a single baseline map or a full-season monitoring program, we'll help you extract real value from every flight. Want to see potential savings? <a href="/roi-calculator" className="text-white hover:text-emerald-200 font-semibold underline">Calculate your ROI</a>.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/instant-quote"
              className="px-8 py-4 bg-emerald-700 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              Get an Instant Quote
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="/#contact"
              className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap border border-white/20"
            >
              Talk to Our Team
            </a>
          </div>
        </div>
      </section>

      {/* Author/Company Section */}
      <section className="py-12 px-6 bg-stone-100 border-t border-stone-200">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-emerald-800 rounded-xl flex items-center justify-center shrink-0">
              <i className="ri-drone-line text-white text-2xl"></i>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Published by</p>
              <p className="font-bold text-gray-900">Greenfield Partners</p>
              <p className="text-sm text-gray-600">
                Precision aerial services for agriculture, forestry, and land management across the Mid-Atlantic region.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gradient bridge into footer */}
      <div className="h-16 bg-gradient-to-b from-stone-100 to-[#1a1f1c]"></div>

      <Footer />
    </div>
  );
}
